import { useState, useEffect } from 'react';
import { adminApi } from '../../lib/admin-api';
import toast from 'react-hot-toast';
import DetailSection from '../shared/DetailSection';
import Badge from '../shared/Badge';
import EmptyState from '../shared/EmptyState';
import AccessTable, { AccessSourceIcons, AccessExpandedDetails } from '../shared/AccessTable';
import {
  getAccessLevelBadgeVariant,
  getAccessSourceIcon,
  getAccessSourceLabel
} from '../../utils/accessHelpers';

// Lock icon SVG path
const LOCK_ICON = 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z';

export default function UserAccessTab({ userId }) {
  const [loading, setLoading] = useState(true);
  const [accessData, setAccessData] = useState(null);

  useEffect(() => {
    if (userId) {
      loadAccessData();
    }
  }, [userId]);

  const loadAccessData = async () => {
    try {
      setLoading(true);
      const data = await adminApi.users.getAccess(userId);
      setAccessData(data);
    } catch (error) {
      console.error('Failed to load access data:', error);
      toast.error('Failed to load product access information');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="flex items-center gap-3 text-gray-400">
          <div className="w-5 h-5 border-2 border-purple-500 border-t-transparent rounded-full animate-spin"></div>
          <span>Loading access information...</span>
        </div>
      </div>
    );
  }

  if (!accessData || !accessData.products || accessData.products.length === 0) {
    return (
      <div className="p-8 max-w-5xl">
        <EmptyState
          icon={LOCK_ICON}
          title="No Product Access"
          subtitle="This user doesn't have access to any products"
        />
      </div>
    );
  }

  const columns = [
    { label: 'Product', className: 'pb-3 pr-4 font-medium' },
    { label: 'Version', className: 'pb-3 px-4 font-medium' },
    { label: 'Access Level', className: 'pb-3 px-4 font-medium' },
    { label: 'Sources', className: 'pb-3 px-4 font-medium' }
  ];

  const renderRow = (product) => (
    <>
      <td className="py-3 pr-4">
        <div>
          <div className="font-medium text-white">{product.product_name}</div>
          {product.product_description && (
            <div className="text-xs text-gray-500 mt-0.5 line-clamp-1">
              {product.product_description}
            </div>
          )}
        </div>
      </td>
      <td className="py-3 px-4">
        <Badge variant="gray" className="text-xs">
          {product.product_version || 'N/A'}
        </Badge>
      </td>
      <td className="py-3 px-4">
        <Badge
          variant={getAccessLevelBadgeVariant(product.access_level)}
          className="text-xs"
        >
          {product.access_level.toUpperCase()}
        </Badge>
      </td>
      <td className="py-3 px-4">
        <AccessSourceIcons
          accessReasons={product.access_reasons}
          getIcon={getAccessSourceIcon}
          getLabel={getAccessSourceLabel}
        />
      </td>
    </>
  );

  const renderExpandedDetails = (product) => (
    <AccessExpandedDetails
      accessReasons={product.access_reasons}
      getIcon={getAccessSourceIcon}
      getLabel={getAccessSourceLabel}
      getBadgeVariant={getAccessLevelBadgeVariant}
    />
  );

  return (
    <div className="p-8 max-w-5xl">
      <div className="space-y-6">
        <DetailSection
          title="Product Access"
          subtitle={`Access to ${accessData.products.length} product${accessData.products.length !== 1 ? 's' : ''}`}
        >
          <AccessTable
            columns={columns}
            data={accessData.products}
            renderRow={renderRow}
            renderExpandedDetails={renderExpandedDetails}
            getRowKey={(product) => product.product_id}
          />
        </DetailSection>
      </div>
    </div>
  );
}
